import { useEffect, useState } from "react";
import Table from "../../../../components/Table";
import MaterialTypography from "../../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { deleteData, fetchData, postData } from "../../../../services";
import UploadFile from "../../../Utils/UploadFile";
import { useSnackbar } from "../../../../context/popup";
import { useLoading } from "../../../../components/Loading";
import { setCurrentPage, setRowsPerPage, setTotalPages } from "./store";
import VariantFilterDialog from './component/VariantFilterDialog.js';

const InsurerVariantList = ({tabState}) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const [search, setSearch] = useState("");
  const { setCircleLoading } = useLoading();
  const [isRefresh, setIsRefresh] = useState(false);
  const currentPage = useSelector(
    (state) => state?.insurerVariantReducer?.currentPage
  );
  const rowsPerPage = useSelector(
    (state) => state?.insurerVariantReducer?.rowsPerPage
  );
  const totalPages = useSelector(
    (state) => state?.insurerVariantReducer?.totalPages
  );

  //dialogstate
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [clearFileOnSuccess, setClearFileOnSuccess] = useState(false);
  const [variantData, setVariantData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    isActive: "",
    gInsurerId: "",
    mVariantId: "",
    iModelId: "",
    iFuelTypeId: "",
    iBodyTypeId: "",
    iBodyStyleId: ""
  });

  const fetchVariantData = async () => {
    setCircleLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: rowsPerPage,
        searchTerm: search,
      });

      // Dynamically add filters only if they have values
      Object.entries(filterData).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });

      const url = `/reference/insurer/variant?${params.toString()}`;

      const response = await fetchData(url);
      setVariantData(response?.data?.items);
      dispatch(setTotalPages(response?.data?.totalPages));
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || error?.message, "error");
    } finally {
      setCircleLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  useEffect(() => {
    if(tabState === "variant"){
      fetchVariantData();
    }
  }, [isRefresh, currentPage, rowsPerPage, search]);

  const columns = [
    {
      key: "sno",
      label: "S.No.",
      renderCell: (row) => row.sno,
    },
    {
      key: "name",
      label: "Variant Name",
      renderCell: (row) => row.name,
    },
    {
      key: "createdAt",
      label: "Created At",
      renderCell: (row) => row.createdAt,
    },
    {
      key: "isActive",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      ),
    },
  ];

  const rows = variantData.map((item, index) => ({
    ...item,
    sno: index + 1,
  }));

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleEdit = (row) => {
    navigate(
      `/insurer-master/insurer-motor-mmv/add-insurer-variant?pageType=edit&id=${row?.id}`
    );
  };

  const handleView = (row) => {
    navigate(
      `/insurer-master/insurer-motor-mmv/add-insurer-variant?pageType=view&id=${row.id}`
    );
  };

  const handleDelete = async (row) => {
    try {
      const response = await deleteData(`/reference/insurer/variant-delete`, {
        ids: [row?.id],
      });
      setIsRefresh(!isRefresh);
      const successResponse = response?.success;
      const responseMessage = response?.message;
      if (successResponse) {
        showSnackbar(responseMessage,"success");
      } else {
        showSnackbar(responseMessage,"error");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showSnackbar(error?.response?.data?.message || "Error deleting data", "error");
    }
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,
    onEdit: (row) => handleEdit(row),
    onView: (row) => handleView(row),
    onDelete: (row) => handleDelete(row),
  };

  const handleAddNewVariant = () => {
    navigate("/insurer-master/insurer-motor-mmv/add-insurer-variant");
  };

  //bulk file upload api function
  const handleFileUploadSuccess = async (file) => {
    setFileUploading(true);
    setUploadSuccess(false);

    try {
      const formData = new FormData();

      formData.append("file", file);

      const result = await postData(
        `/reference/motor/insurer/bulkUpload/insurer-variant`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (result?.success) {
        setUploadSuccess(true);
        showSnackbar(result?.message, "success");
        setOpenUploadFileDialog(false);
        setClearFileOnSuccess(true);
        fetchVariantData();
      } else {
        setUploadSuccess(false);
        showSnackbar("Faiiled to upload file", "error");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      console.error("Upload error:", error || errorMessage);
      showSnackbar(errorMessage, "error");
    } finally {
      setFileUploading(false);
    }
  };

  return (
    <div>
      <CardHeader
        title={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Motor Variant management list
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                View and manage details of all motor variants.
              </MaterialTypography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <MaterialButton
              variant="gradient"
              color="dark"
              size="small"
              startIcon={<FilterAlt/>}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => setOpenUploadFileDialog(true)}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewVariant()}
              >
                Add Variant
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <Card sx={{ mx: 1 }}>
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel={"No Motor Variant data found"}
          handleSearch={handleSearch}
        />
      </Card>

      <UploadFile
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        onUploadSuccess={handleFileUploadSuccess}
        fileTypes={["csv", "xlsx"]}
        fileUploading={fileUploading}
        uploadSuccess={uploadSuccess}
        setUploadSuccess={setUploadSuccess}
        setFileUploading={setFileUploading}
        clearFileOnSuccess={clearFileOnSuccess}
      />

      <VariantFilterDialog
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
      filterData={filterData}
      setFilterData={setFilterData}
      onApply={() => {
          setAnchorEl(null);   // close popup
          fetchVariantData();  // call API after filter applied
      }}
      />
    </div>
  );
};

export default InsurerVariantList;
